import { Component, Input, OnInit } from '@angular/core';
import { faCircleNotch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { OverviewMap } from 'ol/control.js';
import FullScreen from 'ol/control/FullScreen.js';
import MousePosition from 'ol/control/MousePosition.js';
import ScaleLine from 'ol/control/ScaleLine.js';
import Attribution from 'ol/control/Attribution.js'
import Zoom from 'ol/control/Zoom.js';
import { createStringXY } from 'ol/coordinate';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { skip } from 'rxjs';
import { MapAbstractComponent } from '../../../../../../shared/components/MapAbstractComponent';
import { GlobalVars } from '../../../../../../shared/global-vars';
import { IPSData } from '../../../../../../shared/models/ips-data';

@Component({
  selector: 'app-map-desktop',
  templateUrl: './map-desktop.component.html',
  styleUrl: './map-desktop.component.scss'
})
export class MapDesktopComponent extends MapAbstractComponent implements OnInit {
  @Input() sidebarIsOpen: boolean = false;
  ultimatumId!: number;
  listPiezo!: IPSData[];
  faCross = faXmark;
  faSpinner = faCircleNotch;
  previousSelectedDate!: Date;
  layerType: string = GlobalVars.sectorLayersType.simplified;
  firstDateExecution = true;
  firstScenarioExecution = true;
  zoomControl!: Zoom;
  fullScreenControl!: FullScreen;

  ngOnInit(): void {
    this.sidebarService.setSidebarStatus(false);
    this.layerService.currentLayerState$.subscribe((layerState) => {
      this.sensorService.cancelRequests()
      this.updateLayersVisibility(layerState);
    });
    this.sidebarService.sidebarStatus$.subscribe(status => {
      if (!status && this.previousOverlay) {
        if (this.mapService.map)
          this.mapService.map.removeOverlay(this.previousOverlay);
        this.previousOverlay = null;
      }
    });
    
    const overviewControl = new OverviewMap({
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      collapsed: false,
      className: "custom-overview-map"
    });

    this.mapService.createMap('map', [overviewControl], this.refreshSector);

    this.initializeControls();
    this.translate.onLangChange.subscribe(() => {
      this.recreateControls();
    });


    if (this.mapService.map) {
      this.mapService.map.on('moveend', () => {
        if (this.mapService.map) {
          const currentZoom = this.mapService.map.getView().getZoom();
          if(currentZoom)
            this.mapService.updateZoomLevel(currentZoom);
          this.checkZoomAndFetchSensors(false);
  
        }
      });
  
    }
    this.sharedDateService.selectedDate$.subscribe((date: Date) => {
      this.previousSelectedDate = this.selectedDate;
      this.selectedDate = date;
      if (!this.firstDateExecution) {
        this.checkZoomAndFetchSensors(true);
      } else {
        this.firstDateExecution = false;
      }
    });
    this.sharedDateService.selectedScenario$.subscribe((scenario: string) => {
      this.selectedScenario = scenario;
      if (!this.firstScenarioExecution) {
        this.checkZoomAndFetchSensors();
      } else {
        this.firstScenarioExecution = false;
      }
    });
  
    this.mapService.zoomedRefresh$.pipe(skip(1)).subscribe((res) => {
      this.checkZoomAndFetchSensors(true);
    });

    this.authService.userInfoLoaded$.subscribe(isLoaded => {
      if (isLoaded) {
        this.checkZoomAndFetchSensors(false, true);
      }
    });
  }

  recreateControls(): void {
    // Retirer les anciens contrôles
    if (this.mapService.map) {
      this.mapService.map.removeControl(this.zoomControl);
      this.mapService.map.removeControl(this.fullScreenControl);
    }

    // Recréer les contrôles avec les nouvelles traductions
    this.createZoomControl();
    this.createFullScreenControl();
  }

  initializeControls(): void {
    const mousePositionControl = new MousePosition({
      className: "custom-mouse-control",
      coordinateFormat: createStringXY(4),
      projection:'EPSG:4326',
    });

    const scaleLineControl = new ScaleLine({
      className: 'custom-scale-line-control',
      units: 'metric',
    });

    const attributionControl = new Attribution()

    if (this.mapService.map) {
      this.mapService.map.addControl(mousePositionControl);
      this.mapService.map.addControl(scaleLineControl);
      this.mapService.map.addControl(attributionControl);
      this.createZoomControl();
      this.createFullScreenControl();
      }
  }

  // Crée et ajoute le contrôle de zoom avec les labels traduits
  createZoomControl(): void {
    const zoomInLabel = document.createElement('span');
    zoomInLabel.className = 'pi pi-plus';
    const zoomOutLabel = document.createElement('span');
    zoomOutLabel.className = 'pi pi-minus';

    // Traductions des labels
    const zoomInTipLabel = this.translate.instant('MAP.LAYERS.ZOOM_IN');
    const zoomOutTipLabel = this.translate.instant('MAP.LAYERS.ZOOM_OUT');

    // Création du contrôle de zoom
    this.zoomControl = new Zoom({
      className: 'custom-zoom-control',
      zoomInLabel: zoomInLabel,
      zoomOutLabel: zoomOutLabel,
      zoomInTipLabel: zoomInTipLabel,
      zoomOutTipLabel: zoomOutTipLabel,
    });

    if (this.mapService.map) {
      // Ajout du contrôle à la carte
      this.mapService.map.addControl(this.zoomControl);
    }
  }
  
  // Crée et ajoute le contrôle de plein écran avec le label traduit
  createFullScreenControl(): void {
    const expandLabel = document.createElement('span');
    expandLabel.className = 'pi pi-expand';
    const fullscreenTipLabel = this.translate.instant('MAP.LAYERS.FULL_SCREEN');
    this.fullScreenControl = new FullScreen({
      className: 'custom-fullscreen-control',
      label: expandLabel,
      tipLabel: fullscreenTipLabel,
    });

    if (this.mapService.map) {
      this.mapService.map.addControl(this.fullScreenControl);
    }
  }


}